import './SettingsPanelSection.css';

import { TabPanel, TabView } from 'primereact/tabview';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import SeptimLoadingSpinner from '../../../SeptimLoadingSpinner/SeptimLoadingSpinner';
import SettingsPanelItemResolver, {
  SettingsPanelResolverProps,
} from '../SettingsPanelItemResolver/SettingsPanelItemResolver';
import { useMaxTabHeight } from '../../../../../hooks/useMaxTabHeight';
import { useSectionTabState } from '../../../../../hooks/useSectionTabState';
import { useLayoutResolveData } from '../../hooks/useLayoutResolveData';
import { useResolvedItemsVisibility } from '../../../../../hooks/useResolvedItemsVisibility';
import { FormItem } from '../../../../../types/types';

// Normalized section node (normalizeFormItemMap): definition section with its
// child nodes as an array (items and/or groups).
export type SettingsPanelSectionNode = {
  type: 'section';
  name: string;
  caption: string;
  visible?: boolean | string;
  allowVisible?: boolean;
  items: unknown[];
};

interface SettingsPanelSectionProps extends Omit<
  SettingsPanelResolverProps,
  'node'
> {
  sections: SettingsPanelSectionNode[];
}

// Tabs of the panel fields (definition `sections`) — the layout counterpart
// of the dataset form FormSection (PrimeReact TabView, hidden tabs stay
// mounted). Tab `visible` accepts a boolean or a JSONata expression over the
// layout resolve data; allowVisible: false hides the tab outright.
const SettingsPanelSection: FC<SettingsPanelSectionProps> = ({
  sections,
  ...shared
}) => {
  const resolveData = useLayoutResolveData(shared.data, shared.handlerScope);

  // The list variant resolves all tab expressions at once (fail = hidden).
  const { visibleByName, pending } = useResolvedItemsVisibility(
    sections as unknown as FormItem[],
    resolveData
  );

  const visibleTabPanels = useMemo(
    () =>
      sections.filter(
        (section) =>
          section.allowVisible !== false &&
          visibleByName[section.name] !== false
      ),
    [sections, visibleByName]
  );

  const groupKey = useMemo(
    () => sections.map((section) => section.name).join('|'),
    [sections]
  );
  const { datasetId, layoutId, objectId } = useParams<{
    datasetId: string;
    layoutId: string;
    objectId?: string;
  }>();
  // Tab memory is per edited layout and per panel — the object panel keeps its
  // tab across object selection, and the canvas/matrix panels stay separate.
  const tabScopeKey = `layout:${datasetId}:${layoutId}:${objectId ?? 'new'}:${
    shared.handlerScope ?? 'panel'
  }`;
  const [activeIndex, setActiveTab] = useSectionTabState(tabScopeKey, groupKey);
  // No form-init tracking in the editor — the panels are ready immediately.
  const { tabViewRef, minPanelHeight } = useMaxTabHeight(
    activeIndex,
    true,
    visibleTabPanels.length,
    '.settings-panel-section-tab-content-wrapper'
  );

  // Skeleton while the tab expressions resolve for the first time.
  if (pending && visibleTabPanels.length === 0) {
    return (
      <div className="settings-panel-section settings-panel-section-skeleton">
        <SeptimLoadingSpinner />
      </div>
    );
  }

  if (visibleTabPanels.length === 0) {
    return null;
  }

  return (
    <TabView
      ref={tabViewRef}
      renderActiveOnly={false}
      className="settings-panel-section"
      activeIndex={Math.min(
        activeIndex,
        Math.max(0, visibleTabPanels.length - 1)
      )}
      onTabChange={(e): void => setActiveTab(e.index)}
    >
      {visibleTabPanels.map((sectionTab) => (
        <TabPanel
          key={sectionTab.name}
          header={<span className="tab-header">{sectionTab.caption}</span>}
        >
          <div
            style={{ minHeight: minPanelHeight || undefined }}
            className="settings-panel-section-tab-content-wrapper"
          >
            {sectionTab.items.map((child, index) => (
              <SettingsPanelItemResolver
                key={`${(child as FormItem).name}_${index}`}
                node={child}
                {...shared}
              />
            ))}
          </div>
        </TabPanel>
      ))}
    </TabView>
  );
};

export default SettingsPanelSection;
